import React, { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useController, useXR, Hands, DefaultXRControllers } from '@react-three/xr'

// function Player(props) {
//   const { player, isPresenting } = useXR()

//   useEffect(() => {
//     if (isPresenting) {
//       player.position.set(0, 0, 0)
//       player.rotation.set(0, Math.PI, 0)
//     } else {
//       player.position.set(0, 0, 0)
//       player.rotation.set(0, 0, 0)
//     }
//   }, [isPresenting])

//   return (
//     <>
//     </>
//   )
// }

import * as THREE from 'three'

import PropTypes from 'prop-types'
import { OrbitControls } from '@react-three/drei'

const Y_OFFSET = 0.5

export default function Player(props) {
  const fly = props.fly
  const controlsRef = props.controlsRef
  const orbitControlsRef = props.orbitControlsRef
  // const playerRef = useRef()

  const moveLeftRef = useRef(false)
  const moveRightRef = useRef(false)
  const moveForwardRef = useRef(false)
  const moveBackwardRef = useRef(false)

  const camera = useThree(state => state.camera)

  const velocityRef = useRef(new THREE.Vector3())
  const directionRef = useRef(new THREE.Vector3())

  const playerPositionRef = useRef(new THREE.Vector3())
  const cameraDirectionRef = useRef(new THREE.Vector3())

  const canJump = useRef(true)
  const wooglyRef = useRef()
  const cameraRef = useRef()

  const cameraQuat = useRef(new THREE.Quaternion())
  const wooglyQuat = useRef(new THREE.Quaternion())
  const { isPresenting } = useXR()
  const leftController = useController('left')
  const rightController = useController('right')

  // TODO: This is a workaround, pending: https://github.com/mrdoob/three.js/pull/21268
  // Basically all quat uniforms should temporarily be replaced with vec4s
  const leftControllerQuatRef = useRef(new THREE.Quaternion())
  const rightControllerQuatRef = useRef(new THREE.Quaternion())
  const leftControllerPosRef = useRef(new THREE.Vector3())
  const rightControllerPosRef = useRef(new THREE.Vector3())

  const rightControllerThumbstickRef = useRef(new THREE.Vector2())
  const canSnapturn = useRef(true)
  const snapturnRotation = THREE.MathUtils.degToRad(45)

  // TODO: Maybe do this with a hook into session.addEventListener('end')?
  // This is then used to reset things like camera rotation after XR session is done
  const needsXRCleanup = useRef(false)

  const { player } = useXR()
  // useEffect(() => {
  //   player.position.set(0, 1, -0.75);
  // }, [player])

  useEffect(() => {
    // player.parent = playerRef.current

    const onKeyDown = function (event) {
      switch (event.code) {
        case 'ArrowUp':
        case 'KeyW':
          moveForwardRef.current = true
          break

        case 'ArrowLeft':
        case 'KeyA':
          moveLeftRef.current = true
          break

        case 'ArrowDown':
        case 'KeyS':
          moveBackwardRef.current = true
          break

        case 'ArrowRight':
        case 'KeyD':
          moveRightRef.current = true
          break

        case 'Space':
          if (canJump.current === true) velocityRef.current.y = 5
          canJump.current = false
          break
      }
    }

    const onKeyUp = function (event) {
      switch (event.code) {
        case 'ArrowUp':
        case 'KeyW':
          moveForwardRef.current = false
          break

        case 'ArrowLeft':
        case 'KeyA':
          moveLeftRef.current = false
          break

        case 'ArrowDown':
        case 'KeyS':
          moveBackwardRef.current = false
          break

        case 'ArrowRight':
        case 'KeyD':
          moveRightRef.current = false
          break
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)
  }, [])


  useEffect(() => {
    const enter = document.querySelector('#enter')

    orbitControlsRef.current.saveState()

    controlsRef.current.addEventListener('lock', () => {
      enter.style.display = 'none'
      // Disable orbit controls while in pointer lock mode
      orbitControlsRef.current.saveState()
      orbitControlsRef.current.enabled = false

    })
    controlsRef.current.addEventListener('unlock', () => {
      orbitControlsRef.current.reset()
      player.position.set(0, 0, 0)
      orbitControlsRef.current.enabled = true

      enter.style.display = null
    })
  }, [controlsRef.current])

  useFrame(({ camera, scene }, delta) => {
    // Potentially cleanup after XR
    if (needsXRCleanup.current && !isPresenting) {
      // TODO: Make reset more configurable
      orbitControlsRef.current.reset()
      player.position.set(0, 0, 0)
      needsXRCleanup.current = false
    }

    const velocity = velocityRef.current
    const direction = directionRef.current

    // Controls used in flat/ASDF mode
    // if (controlsRef.current.isLocked === true) {
      const moveForward = moveForwardRef.current
      const moveBackward = moveBackwardRef.current
      const moveLeft = moveLeftRef.current
      const moveRight = moveRightRef.current

      direction.y = 0
      direction.z = Number(moveBackward) - Number(moveForward)
      direction.x = Number(moveRight) - Number(moveLeft)
      direction.normalize() // this ensures consistent movements in all directions

      // if (moveForward || moveBackward) velocity.z -= direction.z * 100.0 * delta
      // if (moveLeft || moveRight) velocity.x -= direction.x * 100.0 * delta
     // }

    // TODO: Generalize, tested only on Q1/2
    if (isPresenting) {
      
      // Movement is controlled by the left controller thumbstick
      if (leftController) {
        direction.y = 0
        direction.x = leftController?.inputSource?.gamepad?.axes[2]
        direction.z = leftController?.inputSource?.gamepad?.axes[3]
      }

      if (rightController) {
        const x = rightController?.inputSource?.gamepad?.axes[3]
        const y = rightController?.inputSource?.gamepad?.axes[2]
        rightControllerThumbstickRef.current.set(x, y)

        const angle = rightControllerThumbstickRef.current.angle() // Math.atan2(x, y) + Math.PI
        const length = rightControllerThumbstickRef.current.length()

        // Prevent further snapturns until axis returns to (close enough to) 0
        if (length < 0.5 && !canSnapturn.current) canSnapturn.current = true

        if (canSnapturn.current) {
          // Only do snapturns if axis is very prominent (user intent is clear)
          if (length > 0.95) {
            if (Math.abs(angle - Math.PI / 2.0) < 0.6) {
              player.rotateY(-snapturnRotation)
              canSnapturn.current = false
            } else if (Math.abs(angle - 1.5 * Math.PI) < 0.6) {
              player.rotateY(snapturnRotation)
              canSnapturn.current = false
            }
          }
        }
      }

      // Once we're in VR, cleanup is needed when exiting
      needsXRCleanup.current = true
    }

    // Send move events if the player is controlling with keyboard or is in VR
    if (direction.length() > 0 || isPresenting) {
      // Handle moving with direction that's generated by either keypresses or thumbstick axes
      camera.getWorldQuaternion(cameraQuat.current)
      direction.applyQuaternion(cameraQuat.current)

      const SPEED = 0.15
      const factor = direction.length()
      if (fly) {
        velocity.copy(direction)
        velocity.multiplyScalar(SPEED * 16.66667)
      } else if (factor > 0) {
        // TODO: What is this, really? Must be a better name for it
        const vector2 = new THREE.Vector2()
        vector2.set(direction.x, direction.z)
        vector2.setLength(factor * SPEED * 16.66667)
        velocity.x = vector2.x
        velocity.z = vector2.y
      } else if (isPresenting) {
        // You want to do a hard stop in VR, not gradually decrease speed
        velocity.x = 0
        velocity.z = 0
      } else {
        // But in flat mode, you can slowly decrease speed for smoothness
        velocity.x -= velocity.x * 10.0 * delta
        velocity.z -= velocity.z * 10.0 * delta
      }

      if(!fly) {
        velocity.y = velocity.y - 9.8 * delta
      }

      if (velocity.z !== 0) {
        player.position.z += velocity.z * delta
      }
      if (velocity.x !== 0) {
        player.position.x += velocity.x * delta
      }
      if (velocity.y !== 0) {
        player.position.y += velocity.y * delta
      }

      // If player is below ground, reset to 0 and re-enable jump
      if (player.position.y <= Y_OFFSET && !fly) {
        player.position.y = Y_OFFSET
        velocity.y = 0
        canJump.current = true
      }

      // TODO: Super hacky. :shrug:
      camera.getWorldDirection(cameraDirectionRef.current)
      playerPositionRef.current.copy(player.position)

      playerPositionRef.current.addScaledVector(cameraDirectionRef.current, 1)
      const oldY = orbitControlsRef.current.target.y
      orbitControlsRef.current.target.copy(playerPositionRef.current)
      orbitControlsRef.current.target.y = oldY
      orbitControlsRef.current.target.z -= 1.5

      
      // console.log(orbitControlsRef.current.target)
      // const leftControllerData = {
      //   xL: undefined,
      //   yL: undefined,
      //   zL: undefined,
      //   qL: { x: 0, y: 0, z: 0, w: 1 },
      // }

      // const rightControllerData = {
      //   xR: undefined,
      //   yR: undefined,
      //   zR: undefined,
      //   qR: { x: 0, y: 0, z: 0, w: 1 },
      // }

      // TODO: Generalize. Optimize. :song:
      // if (isPresenting) {
      //   if (leftController) {
          // We do this here (and not only in You.jsx) as we need to use it for
          // our local avatar and to send it across the wire.
          // leftControllerQuatRef.current.copy(
          //   leftController.controller.quaternion
          // )
          // The shader needs the inverse of the quat for the controller rotation,
          // so why not do it here
          // leftControllerQuatRef.current.invert()
          // leftControllerPosRef.current.copy(leftController.controller.position)

          // leftControllerData.xL = leftController.controller.position.x
          // leftControllerData.yL = leftController.controller.position.y
          // leftControllerData.zL = leftController.controller.position.z
          // leftControllerData.qL.x = leftController.controller.quaternion.x
          // leftControllerData.qL.y = leftController.controller.quaternion.y
          // leftControllerData.qL.z = leftController.controller.quaternion.z
          // leftControllerData.qL.w = leftController.controller.quaternion.w
        // }
        // if (rightController) {
        //   rightControllerQuatRef.current.copy(
        //     rightController.controller.quaternion
        //   )
          // The shader needs the inverse of the quat for the controller rotation,
          // so why not do it here
          // rightControllerQuatRef.current.invert()
          // rightControllerPosRef.current.copy(
          //   rightController.controller.position
          // )

          // rightControllerData.xR = rightController.controller.position.x
          // rightControllerData.yR = rightController.controller.position.y
          // rightControllerData.zR = rightController.controller.position.z
          // rightControllerData.qR.x = rightController.controller.quaternion.x
          // rightControllerData.qR.y = rightController.controller.quaternion.y
          // rightControllerData.qR.z = rightController.controller.quaternion.z
      //     // rightControllerData.qR.w = rightController.controller.quaternion.w
      //   }
      // }

      // // TODO: Temp. Improve this. It's too jittery at extremes.
      // const cameraPosition = cameraPositionRef.current
      // const cameraDirection = cameraDirectionRef.current
      // const boxTargetOffset = 3.0

      // cameraRef.current.getWorldPosition(cameraPosition)
      // cameraRef.current.getWorldDirection(cameraDirection) // this is a unit vector

       // Observe a scene or a renderer
      // Observe a scene or a renderer
      // if (typeof __THREE_DEVTOOLS__ !== 'undefined') {
      //   __THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('observe', { detail: scene }));
      //   __THREE_DEVTOOLS__.dispatchEvent(new CustomEvent('observe', { detail: scene.renderer }));
      // }

    }
  })



  return (
    // <group ref={playerRef} position={[0, 0, 0]}>
    <>
      <Hands />
      <DefaultXRControllers />
    </>
  )
    // {/* <primitive
    //     object={camera}
    //     ref={cameraRef}
    //     position={[0, 1, 0]}
    //     near={0.1}
    //     far={1000.0}
    //   />       */}
    // {/* </group> */}
}

Player.propTypes = {
  playerRef: PropTypes.shape({ current: PropTypes.object }),
  controlsRef: PropTypes.shape({ current: PropTypes.object }),
  orbitControlsRef: PropTypes.shape({ current: PropTypes.object }),
  fly: PropTypes.bool,
} 

Player.defaultProps = {
  fly: true,
}
